"use client";

import React, { useEffect, useState, Fragment } from "react";
import Link from "next/link";
import Image from "next/image";
import { Transition } from "@headlessui/react";

import logo from "/public/images/uvercee-logo.svg";

const Navbar: React.FC = () => {
  const [isActive, setActive] = useState<boolean>(false); // mobile menu open/close
  const [isSticky, setSticky] = useState<boolean>(false); // whether to show sticky styling

  const handleToggleMenu = () => {
    setActive(!isActive);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!isActive) {
        setSticky(window.scrollY > 50);
      }
    };

    document.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [isActive]);

  // Prevent scrolling in the background when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isActive ? "hidden" : "";
  }, [isActive]);

  return (
    <>
      <header
        id="navbar"
        className={`
          text-white fixed top-0 left-0 w-full z-20 transition-all
          ${isSticky ? "bg-zinc-900 shadow-lg" : "bg-zinc-900 shadow-lg"}
        `}
      >



{/* TOP BAR: PHONE & WHATSAPP */}
{/* <div className="w-full bg-zinc-900 border-b border-zinc-700 text-center text-xs sm:text-sm uppercase py-2 text-white flex justify-center space-x-4">
  <div>
    <span>PHONE:</span>{" "}
    <a
      href="tel:+4402080792721"
      className="text-green-500 hover:text-green-400 transition-colors duration-200"
    >
      0208 0792 721
    </a>
  </div>
  <div>
    <span>WHATSAPP:</span>{" "}
    <a
      href="https://wa.me/4407740267345"
      target="_blank"
      rel="noopener noreferrer"
      className="text-green-500 hover:text-green-400 transition-colors duration-200"
    >
      07740 267 345
    </a>
  </div>
</div> */}




        {/* MAIN NAVBAR */}
        <div className="container mx-auto max-w-[1200px] flex justify-between items-center px-4 py-4">
          {/* Left side: Logo + Social icons */}
          <div className="flex items-center space-x-4">
            <Link href="/">
              <Image src={logo} alt="Logo" className="h-[30px] w-auto" />
            </Link>
            <div className="hidden lg:flex space-x-6 pl-10">
              <a
                href="https://www.instagram.com/uvercee/"
                className="text-2xl hover:text-green-500 transition"
              >
                <i className="ri-instagram-line"></i>
              </a>
              <a
                href="mailto:info@uvercee.co.uk"
                className="text-2xl hover:text-green-500 transition"
              >
                <i className="ri-mail-line"></i>
              </a>
              <a
                href="https://wa.me/4407740267345"
                className="text-2xl hover:text-green-500 transition"
              >
                <i className="ri-whatsapp-line"></i>
              </a>
            </div>
          </div>

          {/* Right side: Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8">
            <Link
              href="/"
              className="text-[14px] uppercase font-light transition-all duration-100 
                ease-in-out hover:outline hover:outline-1 hover:outline-white 
                hover:outline-offset-8 hover:rounded"
            >
              Home
            </Link>
            <Link
              href="/services/ppf-paint-protection-film"
              className="text-[14px] uppercase font-light transition-all duration-100 
                ease-in-out hover:outline hover:outline-1 hover:outline-white 
                hover:outline-offset-8 hover:rounded"
            >
              PPF
            </Link>
            <Link
              href="/services"
              className="text-[14px] uppercase font-light transition-all duration-100 
                ease-in-out hover:outline hover:outline-1 hover:outline-white 
                hover:outline-offset-8 hover:rounded"
            >
              All Services
            </Link>
            <Link
              href="/quote"
              className="text-[14px] uppercase font-light transition-all duration-100 
                ease-in-out hover:outline hover:outline-1 hover:outline-white 
                hover:outline-offset-8 hover:rounded"
            >
              Quote
            </Link>
            <Link
              href="/blog"
              className="text-[14px] uppercase font-light transition-all duration-100 
                ease-in-out hover:outline hover:outline-1 hover:outline-white 
                hover:outline-offset-8 hover:rounded"
            >
              Blog
            </Link>
            <Link
              href="/about"
              className="text-[14px] uppercase font-light transition-all duration-100 
                ease-in-out hover:outline hover:outline-1 hover:outline-white 
                hover:outline-offset-8 hover:rounded"
            >
              About
            </Link>
            <Link
              href="/contact"
              className="text-[14px] uppercase font-light transition-all duration-100 
                ease-in-out hover:outline hover:outline-1 hover:outline-white 
                hover:outline-offset-8 hover:rounded"
            >
              Contact
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={handleToggleMenu}
            className="lg:hidden text-[24px] focus:outline-none"
          >
            <i className={`ri-${isActive ? "close-line" : "menu-line"}`}></i>
          </button>
        </div>
      </header>

      {/* MOBILE MENU (Slide Down) */}
      <Transition
        show={isActive}
        as={Fragment}
        enter="transition ease-out duration-300"
        enterFrom="transform -translate-y-full"
        enterTo="transform translate-y-0"
        leave="transition ease-in duration-200"
        leaveFrom="transform translate-y-0"
        leaveTo="transform -translate-y-full"
      >
        <div className="fixed inset-0 bg-zinc-900 text-white z-50 overflow-y-auto lg:hidden">
          <div className="flex flex-col h-full">
            {/* Logo + Close Button */}
            <div className="flex justify-between items-center p-4 border-b border-gray-700">
              <Image src={logo} alt="Logo" className="h-[30px] w-auto" />
              <button
                onClick={handleToggleMenu}
                className="text-[30px] focus:outline-none"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Mobile Nav Links */}
            <div className="flex-1 flex flex-col justify-between p-6">
              <nav className="space-y-6">
                <Link
                  href="/"
                  className="block text-[18px] uppercase font-light hover:text-green-500 transition"
                  onClick={handleToggleMenu}
                >
                  Home
                </Link>
                <Link
                  href="/services/ppf-paint-protection-film"
                  className="block text-[18px] uppercase font-light hover:text-green-500 transition"
                  onClick={handleToggleMenu}
                >
                  PPF
                </Link>
                <Link
                  href="/services"
                  className="block text-[18px] uppercase font-light hover:text-green-500 transition"
                  onClick={handleToggleMenu}
                >
                  All Services
                </Link>
                <Link
                  href="/quote"
                  className="block text-[18px] uppercase font-light hover:text-green-500 transition"
                  onClick={handleToggleMenu}
                >
                  Quote
                </Link>
                <Link
                  href="/blog"
                  className="block text-[18px] uppercase font-light hover:text-green-500 transition"
                  onClick={handleToggleMenu}
                >
                  Blog
                </Link>
                <Link
                  href="/about"
                  className="block text-[18px] uppercase font-light hover:text-green-500 transition"
                  onClick={handleToggleMenu}
                >
                  About
                </Link>
                <Link
                  href="/contact"
                  className="block text-[18px] uppercase font-light hover:text-green-500 transition"
                  onClick={handleToggleMenu}
                >
                  Contact
                </Link>
              </nav>

              {/* Contact Area in Mobile Menu */}
              <div className="border-t border-green-500 pt-6 text-left space-y-4">
                <h3 className="text-lg font-semibold text-white">
                  Contact Us <span aria-hidden="true">↗</span>
                </h3>

                <div className="flex items-center space-x-4">
                  <i className="ri-phone-line text-2xl text-white"></i>
                  <div>
                    <p className="text-sm text-white">Phone</p>
                    <a href="tel:+4402080792721" className="text-base text-green-500">
                      0208 0792 721
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <i className="ri-mail-line text-2xl text-white"></i>
                  <div>
                    <p className="text-sm text-white">Email</p>
                    <a href="mailto:info@uvercee.co.uk" className="text-base text-green-500">
                      info@uvercee.co.uk
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                  <div>
                    <p className="text-sm text-white">Address Sevenoaks:</p>
                    <p className="text-base text-green-500">
                    2, Josephine Building The Woodlands, West Kingsdown, Sevenoaks TN15 6AR
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                  <div>
                    <p className="text-sm text-white">Address Bromley:</p>
                    <p className="text-base text-green-500">
                      50D Homesdale Rd, Bromley BR2 9LD
                    </p>
                  </div>
                </div>

                {/* Spacing for icons, etc. */}
                <div className="pt-[45px]" />
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
};

export default Navbar;
